/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Container, Grid } from '@mui/material';
import ErrorBoundary from '../Errors/ErrorBoundary';
import { componentsConfig } from '../Config/ComponentsConfig';
import { ErrorMessage } from '../Errors/ErrorMessage';
import { useStoreAppFrame } from './useStoreAppFrame';
import { FullScreenLoadingOverlay } from '../Components/LoadingOverlay/FullScreenLoadingOverlay';
import ErrorTemplate from '../Errors/ErrorTemplate';

/**
 * StoreAppFrame Component
 *
 * This AppFrame stores query parameter and data attributes to the session storage before the actual component is rendered
 *
 * @param configKey
 * @returns {JSX.Element}
 * @constructor
 */
function StoreAppFrame({ configKey }) {
  const config = componentsConfig[configKey];

  const {
    appFrameQueryParametersInitialized,
    appFrameDataAttributesInitialized,
  } = useStoreAppFrame();

  if (!appFrameQueryParametersInitialized && !appFrameDataAttributesInitialized) {
    return (
      <FullScreenLoadingOverlay />
    );
  }

  // Dynamically import the component based on the configuration
  const MainComponent = React.lazy(() => import(`./${config.content.component}`));
  return (
    <Grid container direction="row">
      {/* add here a static header if necessary */}
      <Grid
        item
        xs
        p="16px 0 80px 0"
        sx={{
          overflowY: 'auto',
          flex: 1,
        }}
      >
        <Container>
          <ErrorBoundary fallback={<ErrorTemplate />}>
            <React.Suspense fallback={<div>Loading...</div>}>
              <MainComponent {...config.content.params} />
            </React.Suspense>
            <ErrorMessage />
          </ErrorBoundary>
        </Container>
      </Grid>
      {/* add here a static footer if necessary */}
    </Grid>
  );
}

StoreAppFrame.propTypes = {
  configKey: PropTypes.string.isRequired,
};

export default StoreAppFrame;
